import { MouseEvent, useCallback, useEffect, useMemo, useState } from 'react';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import { Checkbox, PrimaryNavigationMenu, PrimaryNavigationMenuItem, TertiaryNavigationActionButton } from 'components/ui';
import { toString } from 'utilities/toString';
import { SecurityFilterMenuProps } from './SecurityFilterMenu.props';

interface SecurityFilterOption {
  id: string;
  label: string;
}

const SECURITY_FILTER_TEST_IDS = {
  trigger: 'cash-flow-ledger-filter-by-security-btn',
  option: (id: string) => `cash-flow-ledger-filter-by-security-option-${id}`,
};

const SecurityFilterMenu = ({ cashFlows, setActiveSecurityIds, reset }: SecurityFilterMenuProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedSecurityIds, setSelectedSecurityIds] = useState<string[]>([]);
  const [{ captableInfo }] = useStore() as unknown as UseStoreValues;

  // Only securities actually referenced by a ledger row on this page are offered.
  const securityOptions = useMemo<SecurityFilterOption[]>(() => {
    if (!cashFlows?.length) {
      return [];
    }

    const captableSecurities = captableInfo?.securities ?? [];
    const options = new Map<string, SecurityFilterOption>();

    cashFlows.forEach(row => {
      if (row.security == null) {
        return;
      }
      const id = toString(row.security);
      if (options.has(id)) {
        return;
      }
      const matchedSecurity = captableSecurities.find(security => Number(security.id) === Number(row.security));
      if (matchedSecurity?.name) {
        options.set(id, { id, label: matchedSecurity.name });
      }
    });

    return Array.from(options.values());
  }, [captableInfo?.securities, cashFlows]);

  useEffect(() => {
    if (reset) {
      setSelectedSecurityIds([]);
    }
  }, [reset]);

  // No valid selection yet (initial load/reset) defaults to every security, never to zero.
  const effectiveSecurityIds = useMemo(() => {
    const valid = selectedSecurityIds.filter(id => securityOptions.some(option => option.id === id));
    if (valid.length) {
      return valid;
    }
    return securityOptions.map(option => option.id);
  }, [securityOptions, selectedSecurityIds]);

  useEffect(() => {
    setActiveSecurityIds(effectiveSecurityIds);
  }, [effectiveSecurityIds, setActiveSecurityIds]);

  const handleOpen = useCallback((event: MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget), []);
  const handleClose = useCallback(() => setAnchorEl(null), []);
  const handleToggle = useCallback(
    (option: SecurityFilterOption) => {
      const isChecked = effectiveSecurityIds.includes(option.id);
      if (isChecked) {
        // At least one security must stay selected at all times.
        if (effectiveSecurityIds.length <= 1) {
          return;
        }
        setSelectedSecurityIds(effectiveSecurityIds.filter(id => id !== option.id));
        return;
      }
      setSelectedSecurityIds([...effectiveSecurityIds, option.id]);
    },
    [effectiveSecurityIds]
  );

  if (securityOptions.length <= 1) {
    return null;
  }

  return (
    <>
      <TertiaryNavigationActionButton
        data-testid={SECURITY_FILTER_TEST_IDS.trigger}
        endIcon={<ArrowDropDownIcon fontSize="inherit" />}
        onClick={handleOpen}
        variant="secondary">
        Filter by Security
      </TertiaryNavigationActionButton>
      <PrimaryNavigationMenu anchorEl={anchorEl} onClose={handleClose} open={Boolean(anchorEl)}>
        {securityOptions.map(option => {
          const isActive = effectiveSecurityIds.includes(option.id);
          const isOnlySelection = isActive && effectiveSecurityIds.length === 1;
          return (
            <PrimaryNavigationMenuItem
              aria-selected={isActive}
              data-testid={SECURITY_FILTER_TEST_IDS.option(option.id)}
              isActive={isActive}
              key={option.id}
              leadingSlot={<Checkbox checked={isActive} disabled={isOnlySelection} readOnly visualVariant="selection" />}
              onClick={() => handleToggle(option)}
              role="option">
              {option.label}
            </PrimaryNavigationMenuItem>
          );
        })}
      </PrimaryNavigationMenu>
    </>
  );
};

export default SecurityFilterMenu;
